import React from "react";

import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../app/providers/AuthProvider";
import { Sidebar } from "../../components/layout/Sidebar";
import { Topbar } from "../../components/layout/Topbar";
import { cn } from "../../lib/cn";
import { getAppEnv } from "../../lib/env";

function getPageTitle(pathname: string): string {
  if (pathname === "/" || pathname === "") return "Dashboard";
  if (pathname.startsWith("/company")) return "Company";
  if (pathname.startsWith("/logs")) return "Logs";
  if (pathname.startsWith("/sandbox")) return "Sandbox";
  return "Dashboard";
}

function getEnvBadge(): string {
  const env = getAppEnv();
  return String(env || "dev").toUpperCase();
}

/**
 * Dashboard layout shell:
 * - Sidebar + Topbar + content
 * - Styling uses existing brand CSS variables (`--kkp-*`) from `brand-base.css`
 * - HashRouter-friendly sidebar links are handled by `Sidebar` via `href="#/..."`.
 */
export default function DashboardLayout() {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = React.useState(false);

  const title = React.useMemo(
    () => getPageTitle(location.pathname),
    [location.pathname],
  );

  return (
    <div className="h-screen w-full overflow-hidden bg-[color:var(--kkp-bg)] text-[color:var(--kkp-text)]">
      <div className="box-border flex h-full w-full flex-col gap-3 px-3 py-3 md:flex-row md:gap-4 md:px-4 md:py-4">
        <Sidebar
          pathname={location.pathname}
          user={
            auth.user
              ? { name: auth.user.username, role: auth.user.role }
              : null
          }
          onLogout={() => {
            auth.logout();
            navigate("/login", { replace: true });
          }}
          mobileOpen={mobileNavOpen}
          onMobileOpenChange={setMobileNavOpen}
        />

        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4">
          <Topbar
            title={title}
            subtitle="Admin workspace"
            envBadge={getEnvBadge()}
            userLabel={auth.user?.email ?? ""}
            onMenuClick={() => setMobileNavOpen(true)}
          />

          <main
            className={cn(
              "min-h-0 min-w-0 flex-1 overflow-auto",
              "rounded-[var(--kkp-radius)]",
              "border border-[color:var(--kkp-border)]",
              "bg-[color:var(--kkp-surface)]",
              "p-3 md:p-4",
            )}
          >
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
